import React, { useState, Fragment } from 'react';
import { Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextInput from '@/components/forms/TextInput';
import { useControlledFieldArray } from '@/hooks/useControlledFieldArray';
import { M24MediaModel } from '@/declarations/models/M24MediaModel';
import { useKeyGenerator } from '@/hooks/useKeyGenerator';
import { MediaResourceType } from '@/declarations/models/MediaResourceType';
import { GenericMedia } from '@/declarations/GenericMedia';
import SwitchInput from '../../../../components/forms/SwitchInput';
import Container from '../../../../components/Container';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import FinderButton, { FinderType } from '../../../../components/Finder/FinderButton';
import DropIndicatorArrow from '../../../../components/DropIndicatorArrow';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import AttachmentsBlockItem from './AttachmentsBlockItem';
import { AttachmentsBlock as AttachmentsBlockModel } from '../../../../declarations/models/blocks/AttachmentsBlock';
import Styles from '../../../../assets/js/Styles';

export const AttachmentsBlock: BlockComponent = () => {
  const { t } = useTranslation('common');
  const { t: tComponents } = useTranslation('components');
  const { blockPath } = useCurrentBlock();

  const getAttachmentItemKey = useKeyGenerator<M24MediaModel>((item): string =>
    String(item.id || item.dms_id || item.filename || item.url),
  );

  const {
    fields: selectedMediaItems,
    append,
    insert,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<AttachmentsBlockModel>, `${BlockPath}.items`, 'itemHash'>({
    name: `${blockPath}.items`,
  });

  const handleAddM24Model = (item: M24MediaModel | null) => {
    if (item) {
      append(item);
    }
  };

  const settingsAreaStyle = {
    marginTop: 4,
    width: '100%',
    backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
    padding: 4,
    borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
    gap: 3,
    flexDirection: 'column',
  };

  const [itemsVisible, setItemsVisible] = useState<boolean>(true);

  const handleMove = (fromIndex: number, toIndex: number) => {
    setItemsVisible(false);
    move(fromIndex, toIndex);
    setTimeout(() => {
      setItemsVisible(true);
    }, 100);
  };

  const handelRemove = (index: number) => {
    setItemsVisible(false);
    remove(index);
    setTimeout(() => {
      setItemsVisible(true);
    }, 100);
  };

  return (
    <Container gap={0} column top left fullWidth>
      {(selectedMediaItems || []).map((item, i) => (
        <Fragment key={getAttachmentItemKey(item)}>
          <Collapse sx={{ width: '100%' }}>
            <DropIndicatorArrow
              label={t('Draggable.MoveHere')}
              borderStyle='dashed'
              typographyProps={{
                variant: 'caption',
                style: {
                  lineHeight: '1rem',
                  fontStyle: 'italic',
                },
              }}
              spacing={2}
              visible
              noArrow
            />
          </Collapse>

          {itemsVisible && (
            <AttachmentsBlockItem
              index={i}
              onReorder={handleMove}
              onAdd={(itemToAdd, addAtIndex) => itemToAdd && insert(addAtIndex, itemToAdd)}
              onRemove={handelRemove}
            />
          )}
        </Fragment>
      ))}

      <Container pt={4} fullWidth>
        <FinderButton
          type={FinderType.M24}
          finderProps={{
            onSelectionConfirmed: (items: Array<GenericMedia<M24MediaModel>>) =>
              items.forEach((item) => handleAddM24Model(item.source)),
            defaultResourceType: MediaResourceType.DOCUMENT,
            selectableResourceTypes: [MediaResourceType.DOCUMENT, MediaResourceType.IMAGE, MediaResourceType.AUDIO],
          }}
        />
      </Container>

      <br />

      <Container sx={settingsAreaStyle} fullWidth left p={4} gap={2}>
        <TextInput path={`${blockPath}.rowMax`} label={tComponents('AttachmentsBlock.MaxRows')} type='number' />

        <SwitchInput
          fullWidth
          path={`${blockPath}.listSearch`}
          label={tComponents('AttachmentsBlock.ShowSearchField')}
        />

        <TextInput
          path={`${blockPath}.listSearchText`}
          label={tComponents('AttachmentsBlock.SearchFieldText')}
          type='text'
        />
      </Container>
    </Container>
  );
};

export default AttachmentsBlock;
